"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.aggregateWaterSensors = exports.selectWaterSensors = void 0;
const types_1 = require("../../types");
const alertStatus_1 = require("../alertStatus");
const coverage_1 = require("../coverage");
const sensorClassification_1 = require("../sensorClassification");
const sensorInclusion_1 = require("../sensorInclusion");
/**
 * The water-category sensor filter, lifted out of the hook's `useMemo` so the
 * core can be tested without one.
 *
 * No hubId filter: the caller's `sensors` list is already scoped by context.
 * On the hub display that scope is the HOUSEHOLD (this hub plus its linked
 * gateways), and gateway sensors carry their own hubId — filtering on hubId
 * here would silently drop them.
 */
function selectWaterSensors(sensors) {
    return sensors.filter((s) => (0, sensorInclusion_1.isDashboardSensor)(s) &&
        (s.type === types_1.SensorType.WATER_QUALITY || s.type === types_1.SensorType.LEAK_DETECTOR));
}
exports.selectWaterSensors = selectWaterSensors;
/**
 * Pure water aggregation. Extracted verbatim from `useWaterAggregation`'s
 * `useMemo` body, which was already a pure function of its three deps plus the
 * hysteresis ref.
 *
 * `hysteresis` is the per-sensor "currently flagged for humidity" map and is
 * MUTATED IN PLACE, exactly as the hook's ref was — `isHumidityElevated` is
 * idempotent in `wasElevated`, so a double-invoked render cannot flip the
 * answer. Tests must pass a fresh Map per case or they go order-dependent.
 *
 * `waterSensors` must already be `selectWaterSensors`-filtered.
 */
function aggregateWaterSensors(waterSensors, fetchResults, loading, hysteresis = new Map(), nowSeconds) {
    if (waterSensors.length === 0) {
        return {
            sensors: [],
            sensorCount: 0,
            overallStatus: types_1.SensorCardStatus.Good,
            needsAttentionCount: 0,
            activeAlertCount: 0,
            offlineCount: 0,
            unknownCount: 0,
            coverage: 'full',
            averages: {
                avgHumidity: 0,
                avgQualityLabel: 'No Sensors',
                avgQualityStatus: types_1.SensorCardStatus.Good,
                leakCount: 0,
                humidityAlertCount: 0,
                waterQualitySensorCount: 0,
                leakDetectorCount: 0,
                offlineLeakDetectorCount: 0,
            },
            loading,
        };
    }
    const sensorSummaries = [];
    let overallStatus = types_1.SensorCardStatus.Good;
    let worstSensor;
    let needsAttentionCount = 0;
    let activeAlertCount = 0;
    let humiditySum = 0;
    let humidityCount = 0;
    let leakCount = 0;
    let humidityAlertCount = 0;
    let hasFault = false;
    let offlineWqCount = 0;
    let offlineLdCount = 0;
    const qualityEcValues = [];
    const wqSensors = waterSensors.filter((s) => s.type === types_1.SensorType.WATER_QUALITY);
    const ldSensors = waterSensors.filter((s) => s.type === types_1.SensorType.LEAK_DETECTOR);
    // Process water quality sensors
    wqSensors.forEach((sensor) => {
        const result = fetchResults.get(sensor.id);
        const event = result?.event;
        const coverage = (0, coverage_1.sensorCoverageState)(sensor, nowSeconds);
        const isOffline = coverage === 'offline';
        let status = types_1.SensorCardStatus.Good;
        let value = 'Good';
        let needsAttention = false;
        let attentionMessage;
        let threshold;
        // Offline wins over the last stored reading — a dead sensor's stale EC
        // must not classify as "Good" (or contribute to the average). The status is
        // Offline, NOT Error: nothing is known to be wrong here, we just can't see.
        // Error is reserved for a hazard happening now, and using it for silence is
        // what painted a whole category card red over one dead battery.
        if (isOffline) {
            offlineWqCount++;
            status = types_1.SensorCardStatus.Offline;
            value = 'Offline';
            // Deliberately NO needsAttention / attentionMessage: those feed the amber
            // HAZARD badge. This row is reported through `coverage` → the coverage
            // badge ("Not monitoring — water quality at Main Line isn't being
            // checked"). Setting both rendered the same sensor twice on one card,
            // once styled as a hazard — the channel-mixing Offline exists to end.
        }
        else if (event) {
            const ecValue = (0, sensorClassification_1.getECFromEvent)(event);
            if (ecValue <= 0) {
                status = types_1.SensorCardStatus.Error;
                value = 'Sensor Fault';
                hasFault = true;
                needsAttention = true;
                attentionMessage = `${sensor.location || sensor.name}: Sensor Fault`;
            }
            else {
                threshold = (0, sensorClassification_1.getWaterQualityThreshold)(ecValue);
                qualityEcValues.push(ecValue);
                status = (0, sensorClassification_1.waterQualityThresholdToStatus)(threshold);
                // Row shows the reading (TDS ppm, same 0.6 factor as the gauge); the
                // status badge beside it already carries the band label.
                value = `${Math.round(ecValue * 0.6)} ppm`;
                if (status !== types_1.SensorCardStatus.Good) {
                    needsAttention = true;
                    attentionMessage = `${sensor.location || sensor.name}: ${(0, sensorClassification_1.waterQualityThresholdToLabel)(threshold)}`;
                }
            }
        }
        else {
            // Paired but has never reported, or its fetch result is missing. Without
            // this branch the Good/'Good' initializers above leak through and a
            // sensor that has never sent a reading renders as healthy — the same
            // defect the aggregate-level "No Sensors"/"No Data" split already fixed
            // one layer up.
            //
            // Unknown, not Calibrating. Calibrating must mean the real BSEC warm-up
            // transient only; overloading it for "no history" made a genuinely
            // unknown row indistinguishable from a healthy one at priority 0.
            // Unknown ranks 1 — above Good so it is visible, below Offline and far
            // below any hazard, so it still cannot drag a household card down. It is
            // excluded from qualityEcValues either way. The sweep flips this row to
            // INACTIVE once the per-type threshold expires (180 min for water
            // quality), at which point the isOffline branch above takes over.
            status = types_1.SensorCardStatus.Unknown;
            value = 'Awaiting data';
        }
        // This loop previously ignored the sensor's alerts entirely, so a
        // WQ_SENSOR_FAULT or SENSOR_INACTIVE row left the card green.
        //
        // Two channels, never mixed. STATUS sees every active alert — type-aware,
        // so the sensor's own offline row folds to Offline, never Poor. HAZARD
        // (counts + badge copy) sees only `hazardAlerts` — the offline row is
        // excluded because the coverage badge already carries it.
        const wqAlerts = (0, alertStatus_1.activeAlerts)(result?.alerts ?? []);
        const wqHazards = (0, alertStatus_1.hazardAlerts)(wqAlerts);
        activeAlertCount += wqHazards.length;
        if (wqAlerts.length > 0) {
            status = (0, sensorClassification_1.worstStatus)(status, (0, alertStatus_1.activeAlertsToStatus)(wqAlerts));
        }
        if (wqHazards.length > 0) {
            needsAttention = true;
            attentionMessage = (0, alertStatus_1.alertAttentionMessage)(sensor.location || sensor.name || 'Water Quality', wqHazards);
        }
        const summary = {
            sensorId: sensor.id,
            name: sensor.name || sensor.location || 'Water Quality',
            location: sensor.location || '',
            typeLabel: 'Water Quality',
            sensorType: sensor.type,
            status,
            value,
            needsAttention,
            attentionMessage,
            isOffline,
            coverage,
            lastReportedAt: sensor.lastReported,
            alertCount: wqHazards.length,
        };
        sensorSummaries.push(summary);
        if (needsAttention)
            needsAttentionCount++;
        const newOverall = (0, sensorClassification_1.worstStatus)(overallStatus, status);
        if (newOverall !== overallStatus) {
            overallStatus = newOverall;
            worstSensor = summary;
        }
    });
    // Process leak detector sensors
    ldSensors.forEach((sensor) => {
        const result = fetchResults.get(sensor.id);
        const event = result?.event;
        const alerts = result?.alerts ?? [];
        const coverage = (0, coverage_1.sensorCoverageState)(sensor, nowSeconds);
        const isOffline = coverage === 'offline';
        let status = types_1.SensorCardStatus.Good;
        let value = 'No Leak';
        let needsAttention = false;
        let attentionMessage;
        // Hoisted out of the event branch so the alert fold below can tell
        // whether "Leak Detected" already owns the attention message.
        let leakDetected = false;
        // Offline wins — an offline leak detector can't detect leaks, so "No
        // Leak" + stale humidity would be actively misleading.
        if (isOffline) {
            offlineLdCount++;
            status = types_1.SensorCardStatus.Offline;
            value = 'Offline';
            // No needsAttention / attentionMessage — coverage channel only. See the
            // water-quality branch above.
        }
        else if (event) {
            const humidity = (0, sensorClassification_1.getHumidityFromEvent)(event);
            if (humidity !== null) {
                humiditySum += humidity;
                humidityCount++;
            }
            // Check for active leak
            leakDetected =
                (0, sensorClassification_1.getLeakFromEvent)(event) ||
                    alerts.some((a) => (0, types_1.isAlertActive)(a) && a.alertType?.includes('LEAK'));
            if (leakDetected) {
                status = types_1.SensorCardStatus.Error;
                value = 'Leak Detected';
                leakCount++;
                needsAttention = true;
                attentionMessage = `${sensor.location || sensor.name}: Leak Detected`;
            }
            else if (humidity !== null &&
                (0, types_1.isHumidityElevated)(humidity, hysteresis.get(sensor.id))) {
                // Hysteresis state, keyed per sensor. `isHumidityElevated` is
                // idempotent in `wasElevated`, so a double-invoked render writing this
                // twice cannot flip the answer.
                hysteresis.set(sensor.id, true);
                const displayed = Math.round(humidity);
                status = types_1.SensorCardStatus.Poor;
                value = `${displayed}% High`;
                needsAttention = true;
                attentionMessage = `${sensor.location || sensor.name}: ${displayed}% Humidity`;
                const humidityAlerts = alerts.filter((a) => (0, types_1.isAlertActive)(a) && a.alertType?.includes('HUMIDITY'));
                humidityAlertCount += humidityAlerts.length;
            }
            else if (humidity !== null) {
                hysteresis.set(sensor.id, false);
                value = `${Math.round(humidity)}%`;
            }
        }
        else {
            // No event on file. Same defect the water-quality branch above already
            // guards: without this, a detector that has never reported renders "No
            // Leak" in green, which is the single most misleading thing a leak
            // detector row can say.
            status = types_1.SensorCardStatus.Unknown;
            value = 'Awaiting data';
        }
        // Leak and high-humidity are handled above with their own copy. This
        // catches everything else the detector can raise (low battery, not
        // reporting) that used to leave the row reading "No Leak".
        const ldAlerts = (0, alertStatus_1.activeAlerts)(alerts);
        const ldHazards = (0, alertStatus_1.hazardAlerts)(alerts);
        activeAlertCount += ldHazards.length;
        if (ldAlerts.length > 0) {
            status = (0, sensorClassification_1.worstStatus)(status, (0, alertStatus_1.activeAlertsToStatus)(ldAlerts));
        }
        if (ldHazards.length > 0) {
            needsAttention = true;
            if (!leakDetected) {
                attentionMessage = (0, alertStatus_1.alertAttentionMessage)(sensor.location || sensor.name || 'Leak Detector', ldHazards);
            }
        }
        const summary = {
            sensorId: sensor.id,
            name: sensor.name || sensor.location || 'Leak Detector',
            location: sensor.location || '',
            typeLabel: 'Leak Detector',
            sensorType: sensor.type,
            status,
            value,
            needsAttention,
            attentionMessage,
            isOffline,
            coverage,
            lastReportedAt: sensor.lastReported,
            alertCount: ldHazards.length,
        };
        sensorSummaries.push(summary);
        if (needsAttention)
            needsAttentionCount++;
        const newOverall = (0, sensorClassification_1.worstStatus)(overallStatus, status);
        if (newOverall !== overallStatus) {
            overallStatus = newOverall;
            worstSensor = summary;
        }
    });
    // Compute averages
    const avgHumidity = humidityCount > 0 ? Math.round(humiditySum / humidityCount) : 0;
    // Average quality label + status — fault overrides, then no-sensors vs no-data
    let avgQualityLabel;
    let avgQualityStatus = types_1.SensorCardStatus.Good;
    if (hasFault) {
        avgQualityLabel = 'Sensor Fault';
        avgQualityStatus = types_1.SensorCardStatus.Error;
    }
    else if (qualityEcValues.length > 0) {
        const avgEc = qualityEcValues.reduce((sum, v) => sum + v, 0) / qualityEcValues.length;
        const threshold = (0, sensorClassification_1.getWaterQualityThreshold)(avgEc);
        avgQualityLabel = (0, sensorClassification_1.waterQualityThresholdToLabel)(threshold);
        avgQualityStatus = (0, sensorClassification_1.waterQualityThresholdToStatus)(threshold);
    }
    else if (wqSensors.length === 0) {
        avgQualityLabel = 'No Sensors';
    }
    else if (offlineWqCount === wqSensors.length) {
        // Every water quality sensor is dark, so there is no household water
        // quality to report. "Not monitored" says that; the old bare "Offline" read
        // as a property of the water rather than of our visibility into it, and
        // carrying Error made a coverage gap look like contaminated water.
        avgQualityLabel = 'Not monitored';
        avgQualityStatus = types_1.SensorCardStatus.Offline;
    }
    else {
        avgQualityLabel = 'No Data';
    }
    // Roll the per-row coverage states up rather than recounting: one source of
    // truth for offlineCount / unknownCount / coverage across all four
    // categories, and the never-paired split cannot drift from the row labels.
    const rollup = (0, coverage_1.categoryCoverage)(sensorSummaries);
    return {
        sensors: sensorSummaries,
        sensorCount: waterSensors.length,
        overallStatus,
        needsAttentionCount,
        activeAlertCount,
        offlineCount: rollup.offlineCount,
        unknownCount: rollup.unknownCount,
        coverage: rollup.coverage,
        worstSensor,
        averages: {
            avgHumidity,
            avgQualityLabel,
            avgQualityStatus,
            leakCount,
            humidityAlertCount,
            waterQualitySensorCount: wqSensors.length,
            leakDetectorCount: ldSensors.length,
            offlineLeakDetectorCount: offlineLdCount,
        },
        loading,
    };
}
exports.aggregateWaterSensors = aggregateWaterSensors;
